import { Add, Delete, Edit, Quiz, Visibility, ContentCopy } from "@mui/icons-material";
import BlindsClosedIcon from "@mui/icons-material/BlindsClosed";
import {
  Box,
  Grid,
  IconButton,
  Skeleton,
  Stack,
  TableCell,
  Tooltip,
  Typography,
} from "@mui/material";
import EmptyContent from "components/EmptyContent";
import List from "components/UI/List";
import { useAlert } from "contexts/alertContext";
import { Feature } from "models/api";
import {
  IVendorAssessmentDefinition,
  IVendorAssessmentDefinitionInput,
} from "models/vendorAssessmentDefinition";
import React, { useCallback, useEffect, useState } from "react";
import { QueryClient, useMutation } from "react-query";

import {
  VendorAssessmentDefinitionCells,
  addVendorAssessmentDefinition,
  cloneVendorAssessmentDefinition,
  deleteVendorAssessmentDefinition,
  editVendorAssessmentDefinition,
  getVendorAssessmentDefinitions,
  getWorkshopsByAssessment,
} from "framework/vendorAssessmentDefinition";
import AddVendorAssessmentDefinition from "./partials/AddVendorAssessmentDefinition/AddVendorAssessmentDefinition";
import BlockIcon from "@mui/icons-material/Block";
import DeleteVendorAssessment from "./partials/DeleteVendorAssessmentDefinition/DeleteVendorAssessmentDefinition";
import ViewAssessmentWorkshops from "./partials/ViewAssessmentWorkshops/ViewAssessmentWorkshops";
import { handleError } from "helpers/utils";
import { useTranslation } from "react-i18next";
import { IPermission } from "models/permissions";
import { getPermission } from "helpers";
import { Link } from "react-router-dom";
import EditVendorAssessmentDefinition from "./partials/EditVendorAssessmentDefinition/EditVendorAssessmentDefinition";
import { IAssessmentLevels } from "models/assessmentLevels";
import { getAssessmentLevels } from "framework/assessmentLevels";
import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Button } from "@mui/material";
import { ICompany } from "models/company";
import { getCompaniesByVendor } from "framework/vendor";
import useFetchSection from "hooks/useFetchSection";
import { getCertifyingBodies } from "framework/certifyingBody";
import { ICertifyingBody } from "models/certifyingBody";

interface Props {
  feature: Feature;
}

const VendorAssessmentDefinition: React.FC<Props> = ({ feature }) => {
  const { t, i18n } = useTranslation();
  const [loading2, setLoading2] = useState<boolean>(false);
  const [assessmentLevels, setAssessmentLevels] = useState<IAssessmentLevels[]>(
    [] as IAssessmentLevels[]
  );
  const [FilterVendorAssessment, setFilterVendorAssessment] = useState<
    IVendorAssessmentDefinition[]
  >([] as IVendorAssessmentDefinition[]);
  const [openAdd, setOpenAdd] = useState<boolean>(false);
  const [openEdit, setOpenEdit] = useState<boolean>(false);
  const [openDelete, setOpenDelete] = useState<boolean>(false);
  const [
    selectedVendorAssessmentDefinition,
    setSelectedVendorAssessmentDefinition,
  ] = useState<IVendorAssessmentDefinition>({} as IVendorAssessmentDefinition);
  const [inputObj, setInputObj] = useState<IVendorAssessmentDefinitionInput>(
    {} as IVendorAssessmentDefinitionInput
  );

  const [Companies, setCompanies] = useState<ICompany[]>(
    [] as ICompany[]);
  
  const [CertifyingBodies, setCertifyingBodies] = useState<ICertifyingBody[]>(
    [] as ICertifyingBody[]);
  
  const [openViewWorkshops, setOpenViewWorkshops] = useState<boolean>(false);
  const [workshopsList, setWorkshopsList] = useState<any[]>([]);
  const [loadingWorkshops, setLoadingWorkshops] = useState<boolean>(false);

  const { setAlert } = useAlert();

  // eslint-disable-next-line
  const queryClient = new QueryClient();

  const addVendorAssessmentDefinitionMutation = useMutation({
    mutationFn: (createInput: IVendorAssessmentDefinitionInput) => {
      return addVendorAssessmentDefinition(createInput);
    },
  });
  const [open, setOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string>("");

  const handleOpenDialog = (id: string) => {
    setSelectedId(id);
    setOpen(true);
  };

  const handleCloseDialog = () => {
    setOpen(false);
  };

  const cloneVendorAssessmentDefinitionMutation = useMutation({
    mutationFn: (id: string) => {
      return cloneVendorAssessmentDefinition(id);
    },
  });



  const editIndustriesMutation = useMutation({
    mutationFn: (data: {
      createInput: IVendorAssessmentDefinitionInput;
      id: string;
    }) => {
      return editVendorAssessmentDefinition(data.createInput, data.id);
    },
  });

  const deleteIndustriesMutation = useMutation({
    mutationFn: (data: { id: string }) => {
      return deleteVendorAssessmentDefinition(data.id);
    },
  });


  const {
    loading,
    data: Industries,
    error,
    permission,
    revalidate,
  } = useFetchSection({
    fetchData: () => getVendorAssessmentDefinitions(),
    fetchPermission: () => getPermission(39),
    section: "get assessment definitions",
    onSuccess: (data) => setFilterVendorAssessment(data),
    processResponse: (response) => response && response.length > 0
      ? response.map((item: IVendorAssessmentDefinition) => ({
        ...item,
      }))
      : []
  });

  const fetchAssessmentLevels = useCallback(async () => {
    setLoading2(true);
    try {
      const res = await getAssessmentLevels("/getlevels");
      setAssessmentLevels(res);
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }

    // eslint-disable-next-line
  }, [queryClient, setAlert]);

  const fetchClients = useCallback(async () => {
    setLoading2(true);
    try {
      const res = await getCompaniesByVendor("/getcompanies");
      setCompanies(res);
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }

    // eslint-disable-next-line
  }, [queryClient, setAlert]);

  const fetchCertifyingBodies = useCallback(async () => {
    setLoading2(true);
    try {
      const res = await getCertifyingBodies();
      setCertifyingBodies(res);
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }

    // eslint-disable-next-line
  }, [queryClient, setAlert]);

  useEffect(() => {
    fetchAssessmentLevels();
    fetchClients();
    fetchCertifyingBodies();
    // eslint-disable-next-line
  }, []);

  if (loading || loading2)
    return (
      <Stack spacing={2} p={2}>
        {Array.from({ length: 10 }).map((_, i) => (
          <Skeleton variant="rectangular" key={i} width="100%" height={30} />
        ))}
      </Stack>
    );

  const handleChange = (name: string, value: any) => {
    if (name == "isActive" && value == null) {
      value = false;
    }
    
    // For SELECT inputs, extract the value from the option object
    if (value && typeof value === 'object' && 'value' in value) {
      value = value.value;
    }
    
    setInputObj((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleOpenAdd = () => setOpenAdd(true);
  const handleCloseAdd = () => setOpenAdd(false);

  const handleOpenEdit = (VendorAssessment: IVendorAssessmentDefinition) => {
    setSelectedVendorAssessmentDefinition(VendorAssessment);
    setOpenEdit(true);
  };
  const handleCloseEdit = () => {
    setOpenEdit(false);
    setSelectedVendorAssessmentDefinition({} as IVendorAssessmentDefinition);
  };

  const handleOpenDelete = (VendorAssessment: IVendorAssessmentDefinition) => {
    setSelectedVendorAssessmentDefinition(VendorAssessment);
    setOpenDelete(true);
  };

  const handleCloseDelete = () => {
    setOpenDelete(false);
    setSelectedVendorAssessmentDefinition({} as IVendorAssessmentDefinition);
  };

  const handleOpenViewWorkshops = async (VendorAssessment: IVendorAssessmentDefinition) => {
    setSelectedVendorAssessmentDefinition(VendorAssessment);
    setOpenViewWorkshops(true);
    setLoadingWorkshops(true);
    try {
      const workshops = await getWorkshopsByAssessment(VendorAssessment.id);
      setWorkshopsList(workshops);
      setLoadingWorkshops(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoadingWorkshops(false);
    }
  };

  const handleCloseViewWorkshops = () => {
    setOpenViewWorkshops(false);
    setWorkshopsList([]);
    setSelectedVendorAssessmentDefinition({} as IVendorAssessmentDefinition);
  };

  const getBodyCells = (row: IVendorAssessmentDefinition) => {
    // Start with internalId as the first cell
    const cells: any[] = [
      {
        id: "internalId",
        element: (
          <TableCell align={"left"} key="internalId">
            <Typography variant="body2">{row.internalId || "-"}</Typography>
          </TableCell>
        ),
      },
    ];

    // Then add the rest of the cells
    Object.keys(row).forEach((key: string, i: number) => {
      // Skip internalId as we already added it
      if (key === "internalId") return;

      if (key === "title")
        cells.push({
          id: "title",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row.title}</Typography>
            </TableCell>
          ),
        });

      if (key === "titleAr")
        cells.push({
          id: "titleAr",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row.titleAr}</Typography>
            </TableCell>
          ),
        });

      if (key === "assessmentLevel")
        cells.push({
          id: "assessmentLevel",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">
                {
                  assessmentLevels.find(
                    (level) => level.id === row.assessmentLevel
                  )?.name
                }
              </Typography>
            </TableCell>
          ),
        });

      if (key === "companyId")
        cells.push({
          id: "companyId",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">
                {
                  row.companyName
                }
              </Typography>
            </TableCell>
          ),
        });

      if (key === "certifyingBodyId")
        cells.push({
          id: "certifyingBodyId",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">
                {
                  row.certifyingBodyName || "-"
                }
              </Typography>
            </TableCell>
          ),
        });

      if (key === "createdOn")
        cells.push({
          id: "createdOn",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">
                {new Date(row.createdOn).toLocaleDateString()}
              </Typography>
            </TableCell>
          ),
        });

      if (key === "numberOfQuestions")
        cells.push({
          id: "numberOfQuestions",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">
                {row.numberOfQuestions}
              </Typography>
            </TableCell>
          ),
        });

      if (key === "numberOfWorkshops")
        cells.push({
          id: "numberOfWorkshops",
          element: (
                   <TableCell
  align="left"
  key={i}
>
  <Box
    component={Link}
    to={`/vendorAssessmentWorkshops/${row.id}`}
    sx={{
      display: 'flex',           
      alignItems: 'center',
      width: '100%',
      height: '80px',
      textDecoration: 'none',
      color: 'inherit',
      px: 2,                     
      py: 1,
      '&:hover': {
        backgroundColor: 'action.hover', 
      },
    }}
  >
    <Typography variant="body2">
      {row.numberOfWorkshops}
    </Typography>
  </Box>
</TableCell>

            
          ),
        });

      if (key === "id")
        cells.push({
          id: "actions",
          element: (
            <TableCell align={"right"} key={i}>
              <Stack
                direction={"row"}
                alignContent={"center"}
                justifyContent={i18n.language === "en" ? "start" : "end"}
                spacing={1}
              >
                {permission?.hasModifyPermission && (
                  <Tooltip title={t("T_edit")}>
                    <IconButton
                      size={"small"}
                      color={"primary"}
                      onClick={() => handleOpenEdit(row)}
                    >
                      <Edit />
                    </IconButton>
                  </Tooltip>
                )}

                {permission?.hasDeletePermission && (
                  <Tooltip title={t("T_delete")}>
                    <IconButton
                      size={"small"}
                      color={"error"}
                      onClick={() => handleOpenDelete(row)}
                    >
                      <Delete />
                    </IconButton>
                  </Tooltip>
                )}

                {permission?.hasModifyPermission && (
                  <Tooltip title={t("T_questions")}>
                    <Link to={`/vendorAssessmentQuestion/${row.id}`}>
                      <IconButton size={"small"} color={"info"}>
                        <Quiz />
                      </IconButton>
                    </Link>
                  </Tooltip>
                )}

                {permission?.hasReadPermission && (
                  <Tooltip title={t("T_view_workshops")}>
                    <IconButton
                      size={"small"}
                      color={"success"}
                      onClick={() => handleOpenViewWorkshops(row)}
                    >
                      <Visibility />
                    </IconButton>
                  </Tooltip>
                )}

                {permission?.hasModifyPermission && (
                  <Tooltip title={t("T_clone")}>
                    <IconButton
                      size={"small"}
                      color={"secondary"}
                      onClick={() => handleOpenDialog(row.id)}
                    >
                      <ContentCopy />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            </TableCell>
          ),
        });
    });
    return cells;
  };

  const handleAdd = async () => {
    if (!inputObj.title  || !inputObj.assessmentLevel)
      return setAlert({
        open: true,
        message: "Please fill all fields",
        type: "error",
      });
    try {
      setLoading2(true);

      // Convert empty string to null for companyId and certifyingBodyId
      const createInput: IVendorAssessmentDefinitionInput = {
        ...inputObj,
        companyId: inputObj.companyId === '' ? null : inputObj.companyId,
        certifyingBodyId: inputObj.certifyingBodyId === '' ? null : inputObj.certifyingBodyId
      };

      await addVendorAssessmentDefinitionMutation.mutateAsync(createInput);
      setAlert({
        open: true,
        message: "VendorAssessment added successfully",
        type: "success",
      });
      revalidate();
      handleCloseAdd();
      setInputObj({} as IVendorAssessmentDefinitionInput);
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleEdit = async () => {
    try {
      setLoading2(true);
      
      // Handle companyId: convert empty string to null, otherwise use provided value or existing value
      let companyIdValue;
      if (Object.hasOwn(inputObj, 'companyId')) {
        companyIdValue = inputObj.companyId === '' ? null : inputObj.companyId;
      } else {
        companyIdValue = selectedVendorAssessmentDefinition.companyId || null;
      }
      
      // Handle certifyingBodyId: convert empty string to null, otherwise use provided value or existing value
      let certifyingBodyIdValue;
      if (Object.hasOwn(inputObj, 'certifyingBodyId')) {
        certifyingBodyIdValue = inputObj.certifyingBodyId === '' ? null : inputObj.certifyingBodyId;
      } else {
        certifyingBodyIdValue = selectedVendorAssessmentDefinition.certifyingBodyId || null;
      }
      
      let createInput: IVendorAssessmentDefinitionInput = {
        title: inputObj.title || selectedVendorAssessmentDefinition.title,
        titleAr: Object.hasOwn(inputObj, 'titleAr') ? inputObj.titleAr : selectedVendorAssessmentDefinition.titleAr,
        assessmentLevel: inputObj.assessmentLevel || parseInt(selectedVendorAssessmentDefinition.assessmentLevel),
        companyId: companyIdValue,
        certifyingBodyId: certifyingBodyIdValue
      };
      
      await editIndustriesMutation.mutateAsync({
        createInput,
        id: selectedVendorAssessmentDefinition.id,
      });
      setAlert({
        open: true,
        message: "VendorAssessment updated successfully",
        type: "success",
      });
      revalidate();
      handleCloseEdit();
      setInputObj({} as IVendorAssessmentDefinitionInput);
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleDelete = async () => {
    try {
      setLoading2(true);
      await deleteIndustriesMutation.mutateAsync({
        id: selectedVendorAssessmentDefinition.id,
      });
      setAlert({
        open: true,
        message: "VendorAssessment deleted successfully",
        type: "success",
      });
      revalidate();
      handleCloseDelete();
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };


  const handleConfirmClone = async () => {
    try {
      setLoading2(true);
      await cloneVendorAssessmentDefinitionMutation.mutateAsync(selectedId);
      setAlert({
        open: true,
        message: "VendorAssessment cloned successfully",
        type: "success",
      });
      revalidate();
      handleCloseDialog()
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  return (
    <>
      <Stack spacing={2} p={2}>
        <Grid
          container
          justifyContent={"space-between"}
          alignItems="center"
          sx={{ mb: 4 }}
        >
          <Typography variant="h3" color={"header.primary"}>
            {t(feature.name)}
          </Typography>
          <Stack direction={"row"} spacing={1}>
            {permission?.hasModifyPermission && (
              <Tooltip title={t("T_add")}>
                <IconButton
                  sx={{
                    // bgcolor: 'primary.main',
                    color: "primary.main",
                    "&:hover": {
                      bgcolor: "primary.light",
                      color: "white",
                    },
                    height: 40,
                    width: 40,
                    transition: "0.5s",
                  }}
                  onClick={handleOpenAdd}
                >
                  <Add />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Grid>

        {permission?.hasReadPermission && FilterVendorAssessment.length > 0 && (
          <List
            headCells={VendorAssessmentDefinitionCells}
            data={FilterVendorAssessment}
            getBodyCells={getBodyCells}
            feature={feature}
            canEdit={false}
          />
        )}
        {(!FilterVendorAssessment || FilterVendorAssessment.length === 0) &&
          permission?.hasReadPermission! && (
            <EmptyContent
              icon={<BlindsClosedIcon sx={{ fontSize: "3.5rem " }} />}
              header={t("T_no_assessments")}
              message={t("T_no_assessments")}
            />
          )}
        {!permission?.hasReadPermission && (
          <EmptyContent
            icon={<BlockIcon sx={{ fontSize: "3.5rem " }} />}
            header={t("T_no_permissions")}
            message={t("T_no_permissions_message")}
          />
        )}
      </Stack>

      {openAdd && (
        <AddVendorAssessmentDefinition
          open={openAdd}
          loading={loading2}
          assessmentLevels={assessmentLevels}
          companies={Companies}
          certifyingBodies={CertifyingBodies}
          onClose={handleCloseAdd}
          onSubmit={handleAdd}
          onChanges={handleChange}
        />
      )}
      {openEdit && (
        <EditVendorAssessmentDefinition
          open={openEdit}
          loading={loading2}
          vendorAssessmentDefinition={selectedVendorAssessmentDefinition}
          assessmentLevels={assessmentLevels}
          companies={Companies}
          certifyingBodies={CertifyingBodies}
          onClose={handleCloseEdit}
          onSubmit={handleEdit}
          onChanges={handleChange}
        />
      )}
      {openDelete && (
        <DeleteVendorAssessment
          open={openDelete}
          loading={loading2}
          onClose={handleCloseDelete}
          onSubmit={handleDelete}
        />
      )}

      {openViewWorkshops && (
        <ViewAssessmentWorkshops
          open={openViewWorkshops}
          loading={loadingWorkshops}
          workshops={workshopsList}
          assessmentTitle={selectedVendorAssessmentDefinition.title || ""}
          onClose={handleCloseViewWorkshops}
        />
      )}

      <Dialog open={open} onClose={handleCloseDialog}>
        <DialogTitle>Confirm Clone</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to clone this item?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmClone} color="error" autoFocus>
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default VendorAssessmentDefinition;
